import { useEffect, useRef, useState } from 'react';
import { apiService } from '../services/api';
import { checkAndFixToken } from '../utils/browserTokenCheck';
import { getValidToken } from '../utils/tokenUtils';

/**
 * Статус «Подключен / Отключен» боковой панели (D35, владелец 01.11: «иногда после перезахода в браузер горит
 * красным»). Прежде он проверялся один раз за загрузку страницы: страница, открытая без действующего токена, проверяла
 * без него и красила «Отключен», а вход формой на месте уходит на «Прокси» без перезагрузки — красное держалось до F5
 * (боевой менеджер 18.10–01.11: 53 входа из 157). Теперь до первого ответа — «Проверка…»; проверка повторяется сразу
 * после входа, при возврате на вкладку и раз в 30 с (как счётчик поддержки), после неудачи — через 5 с. Неудача
 * известный ответ не перекрашивает.
 */
export const KRAKEN_STATUS = {
  checking: 'checking',
  connected: 'connected',
  disconnected: 'disconnected',
};

export const KRAKEN_STATUS_LABEL = {
  [KRAKEN_STATUS.checking]: 'Проверка…',
  [KRAKEN_STATUS.connected]: 'Подключен',
  [KRAKEN_STATUS.disconnected]: 'Отключен',
};

const RECHECK_MS = 30000;
const RETRY_MS = 5000;
const REQUEST_TIMEOUT_MS = 8000;

// Демо-вход — как прежде, но один раз за страницу: у живого бэкенда его нет (405), а проверка теперь повторяется.
const resolveToken = async (allowDemoLogin) => {
  checkAndFixToken();
  const token = getValidToken();
  if (token || !allowDemoLogin || localStorage.getItem('skip_demo_login') === 'true') {
    return token;
  }
  try {
    const loginResponse = await apiService.demoLogin();
    const newToken = loginResponse?.data?.access_token;
    if (!newToken) {
      return null;
    }
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify({
      username: 'demo_user',
      role: 'admin'
    }));
    return newToken;
  } catch {
    return null;
  }
};

/** `true` / `false` — ответ «есть ли серверы»; `null` — ответа нет (нет токена или не ответили оба запроса). */
export const fetchKrakenConnected = async ({ allowDemoLogin = false } = {}) => {
  const token = await resolveToken(allowDemoLogin);
  if (!token) {
    return null;
  }
  try {
    const response = await apiService.getKrakenServers({
      timeout: REQUEST_TIMEOUT_MS,
      headers: { 'X-Debug-Source': 'app-kraken-status' },
      params: { debug_source: 'app-kraken-status' }
    });
    const servers = response?.data?.servers;
    return Array.isArray(servers) && servers.length > 0;
  } catch {
    // Разовая ошибка /api/kraken/servers — запасной лёгкий список подключений (D36: параметры и конфиг порознь).
    try {
      const fallbackResponse = await apiService.getActiveKrakenConnections(
        { light: true },
        { timeout: REQUEST_TIMEOUT_MS }
      );
      const connections = Array.isArray(fallbackResponse?.data?.connections)
        ? fallbackResponse.data.connections
        : [];
      return connections.some((connection) => Boolean(connection?.is_active));
    } catch {
      return null;
    }
  }
};

/** Статус для боковой панели и мобильных шапки и меню; `enabled` — не на странице входа. */
export const useKrakenStatus = (enabled) => {
  const [status, setStatus] = useState(KRAKEN_STATUS.checking);
  const demoLoginTriedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    let stopped = false;
    let timer = null;
    let inFlight = false;
    let rerun = false;
    let checkedToken;

    const check = async () => {
      window.clearTimeout(timer);
      timer = null;
      // Скрытая вкладка не опрашивает — проверит возврат на неё.
      if (stopped || document.visibilityState === 'hidden') {
        return;
      }
      if (inFlight) {
        rerun = true;
        return;
      }
      inFlight = true;
      checkedToken = localStorage.getItem('token');
      const allowDemoLogin = !demoLoginTriedRef.current;
      demoLoginTriedRef.current = true;
      const connected = await fetchKrakenConnected({ allowDemoLogin });
      inFlight = false;
      if (stopped) {
        return;
      }
      if (connected !== null) {
        setStatus(connected ? KRAKEN_STATUS.connected : KRAKEN_STATUS.disconnected);
      }
      if (rerun) {
        rerun = false;
        check();
        return;
      }
      timer = window.setTimeout(check, connected === null ? RETRY_MS : RECHECK_MS);
    };

    // Вход формой на месте страницу не перезагружает: новый токен — сразу новая проверка.
    const handleUserUpdated = () => {
      if (localStorage.getItem('token') !== checkedToken) {
        check();
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        check();
      }
    };

    window.addEventListener('user-updated', handleUserUpdated);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    check();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      window.removeEventListener('user-updated', handleUserUpdated);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled]);

  return status;
};
